/* eslint-disable no-undef */
import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CoreWeaveLoader from '../CoreWeave/CoreWeaveLoader/CoreWeaveLoader';
import { useMytaverseEvent } from '../../../providers';
import { useMytaStreams } from './MytaStreamsProvider';
import PlayScreen, { PlayScreenLoading } from '../../PlayScreen';
import { useEventContext } from '@providers/EventProvider';
import { useConference } from '../Dolby';
import { useStyles } from '../CoreWeave/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

export const PixelStreamingWrapper = () => {
  const videoParent = useRef<HTMLDivElement>(null);
  const {
    currentRoom,
    ue5WebsocketConnected,
    sharingWindowFullsceen,
    setSharingWindowFullscreen,
    handleMinimizeSharingScreen,
    isMinimizedScreenSharing,
  } = useMytaverseEvent();
  const {
    mytaLoading,
    dataChannelOpened,
    playStream,
    togglePlayStream,
    isPlayButtonPressed,
    rootElement,
    leaseStatus,
    leaseError,
  } = useMytaStreams();
  const { setIsEventRun } = useEventContext();

  const { screenMediaStreams } = useConference();
  const classes = useStyles();

  const readyToPlay = ue5WebsocketConnected || dataChannelOpened;
  const showLeaseIssue =
    !dataChannelOpened &&
    !!leaseError &&
    ['waiting_slot', 'unconfigured', 'error'].includes(leaseStatus);

  useEffect(() => {
    if (
      mytaLoading ||
      !videoParent.current ||
      !rootElement ||
      !dataChannelOpened ||
      !playStream
    ) {
      return;
    }

    videoParent.current.appendChild(rootElement);
  }, [mytaLoading, dataChannelOpened, rootElement, videoParent, playStream]);

  React.useEffect(() => {
    if (playStream) {
      setIsEventRun(true);
      window.dispatchEvent(new Event('resize'));
    }
  }, [playStream]);

  const leaseIssueBanner = showLeaseIssue ? (
    <Typography
      sx={{
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        color: '#fff',
        background: 'rgba(0,0,0,0.6)',
        borderRadius: 1,
        px: 2,
        py: 0.5,
        fontSize: 14,
      }}
    >
      {leaseError}
    </Typography>
  ) : null;

  if (mytaLoading && !currentRoom) {
    return (
      <>
        <CoreWeaveLoader />
        {leaseIssueBanner}
      </>
    );
  }

  return currentRoom ? (
    <>
      <Box
        id="mytaStreamsWrapper"
        sx={{
          width: '100vw',
          height: '100vh',
          position: 'relative',
          zIndex: playStream ? 10 : -10,
          display: playStream ? 'block' : 'hidden',
        }}
        ref={videoParent}
      ></Box>
      {screenMediaStreams.length && !isMinimizedScreenSharing ? (
        <>
          <IconButton
            sx={classes.fullscreen}
            id="gameFull"
            onClick={setSharingWindowFullscreen}
          >
            {sharingWindowFullsceen ? (
              <FullscreenExitIcon />
            ) : (
              <FullscreenIcon />
            )}
          </IconButton>
          <IconButton
            sx={classes.fullscreenCollapse}
            id="gameCollapse"
            onClick={handleMinimizeSharingScreen}
          >
            <CloseFullscreenIcon />
          </IconButton>
        </>
      ) : null}

      {!playStream && !readyToPlay && isPlayButtonPressed ? (
        <PlayScreenLoading />
      ) : null}
      {!playStream && (!isPlayButtonPressed || readyToPlay) ? (
        <PlayScreen
          buttonHandler={togglePlayStream}
          isDisabled={!readyToPlay}
        />
      ) : null}
      {leaseIssueBanner}
    </>
  ) : (
    <>
      <CoreWeaveLoader />
      {leaseIssueBanner}
    </>
  );
};
